import { useCallback, useEffect, useRef, useState } from "react";
import { ChatHistory, Conversation, ConversationGroup } from "../api/models";
import { useAppContext } from "../hooks/useAppContext";
import Ellipsis from "./SVGIcons/Ellipsis";
import Pin from "./SVGIcons/Pin";
import SideBarHistoryMenu from "./SideBarHistoryMenu";
import {
  deleteChatApi,
  getChatHistory,
  pinChatApi,
  updateChatNameApi,
} from "../api/chatAPI";
import { Brand, Page } from "../context/AppProvider";
import { AxiosError } from "axios";
import translation from "../locales/sidebar.json";
import useBoundStore from "../store/useBoundStore";
import Spinner from "./SVGIcons/Spinner";
import BrandButton from "./BrandButton";
import { ClearChatButton } from "./ClearChatButton";
import ModalBrand from "./ModalBrand";
import BrandIcon from "./BrandIcon";
import { Transition } from "@headlessui/react";
import Ani from "./SVGIcons/Ani";
import { HamburgerIcon } from "@clovirtualfashion/components";
import Tooltip from "./Tooltip";
import Modal from "./ui/Modal";
import ModalDelete from "./ModalDelete";
import useWindowWidth from "../hooks/useWindowWidth";

type SideBarHistoryProps = {
  isChatHistoryLoading?: boolean;
  handleChatConversation: (conversationGroup: ConversationGroup) => void;
  clearChat: () => void;
};

const SideBarHistory = ({
  isChatHistoryLoading,
  handleChatConversation,
  clearChat,
}: SideBarHistoryProps) => {
  const { brand, locale } = useAppContext();
  const user = useBoundStore((state) => state.user);
  const setLastQuestion = useBoundStore((state) => state.setLastQuestion);
  const answers = useBoundStore((state) => state.answers);
  const setAnswers = useBoundStore((state) => state.setAnswer);
  const setConversationID = useBoundStore((state) => state.setId);
  const error = useBoundStore((state) => state.error);
  const setError = useBoundStore((state) => state.setError);
  const setPage = useBoundStore((state) => state.setPage);
  const setCitation = useBoundStore((state) => state.setCitation);
  const chatHistory = useBoundStore((state) => state.chatHistory);
  const setChatHistory = useBoundStore((state) => state.setChatHistory);
  const chatHistoryPanel = useBoundStore((state) => state.chatHistoryPanel);
  const setChatHistoryPanel = useBoundStore(
    (state) => state.setChatHistoryPanel,
  );
  const width = useWindowWidth();
  const [disableLoadMore, setDisableLoadMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [clickedIndex, setClickedIndex] = useState("");
  const [menuId, setMenuId] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [showModalBrand, setShowModalBrand] = useState(false);
  const [renameChat, setRenameChat] = useState(false);
  const [pageHistory, setPageHistory] = useState(1);
  const loadingRef = useRef(false);
  const disableScrollRef = useRef(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const brandButtonRef = useRef<HTMLDivElement>(null);
  const brandIconRef = useRef<HTMLDivElement>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteChatId, setDeleteChatId] = useState("");

  /**
   * Deletes a conversation from the chat history and updates the `chatHistory` context.
   * @param {string[]} chatIds - An array of chat IDs to delete.
   */
  const handleConversationDelete = async (conversationId: string) => {
    try {
      await deleteChatApi(brand, conversationId);

      const newChatHistory = { ...chatHistory };

      Object.keys(newChatHistory).forEach((key) => {
        // Filter out the chat with the given ID from each group
        newChatHistory[key as keyof ChatHistory] = newChatHistory[
          key as keyof ChatHistory
        ].filter((item) => item._id !== conversationId);
      });

      // Filter out empty groups
      Object.keys(newChatHistory).forEach((key) => {
        if (newChatHistory[key as keyof ChatHistory].length === 0) {
          delete newChatHistory[key as keyof ChatHistory];
        }
      });

      setChatHistory(newChatHistory as ChatHistory);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Updates the name of a chat in the chat history.
   * @param {string} chatId - The ID of the chat to update.
   * @param {string} newName - The new name for the chat.
   */
  const handleRename = async (chatId: string, newName: string) => {
    try {
      // Call the update chat name API
      await updateChatNameApi(brand, chatId, newName);

      // Clone the chat history
      const newChatHistory = { ...chatHistory };

      Object.keys(newChatHistory).forEach((key) => {
        // Update the name of the chat with the given ID
        newChatHistory[key as keyof ChatHistory].forEach((item) => {
          item.conversations.forEach((conversation) => {
            if (conversation._id === chatId) {
              conversation.question_summarization = newName;
            }
          });
        });
      });

      // Update the chat history context
      setChatHistory(newChatHistory as ChatHistory);
    } catch (error) {
      console.log(error);
    }
  };

  const getTimeBucketKey = (group: ConversationGroup): string => {
    const lastConv = group.conversations[group.conversations.length - 1];
    const createdAt = lastConv?.created_at;
    if (!createdAt) return "today";

    const convDate = new Date(createdAt.replace(" ", "T") + "Z");
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const diffDays = (todayUTC - convDate.getTime()) / 86400000;

    if (diffDays < 1) return "today";
    if (diffDays < 2) return "yesterday";
    if (diffDays < 7) return "last_7_days";
    if (diffDays < 30) return "last_30_days";

    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    const convYear = convDate.getUTCFullYear();
    if (convYear === now.getUTCFullYear()) return monthNames[convDate.getUTCMonth()];
    return String(convYear);
  };

  const handlePinChat = async (group: ConversationGroup, isPinned: boolean) => {
    const previousHistory = chatHistory;
    const newHistory = { ...(chatHistory ?? {}) } as ChatHistory;

    if (isPinned) {
      // Remove from its time bucket
      Object.keys(newHistory).forEach((key) => {
        if (key !== "pinned") {
          newHistory[key] = newHistory[key].filter((item) => item._id !== group._id);
          if (newHistory[key].length === 0) delete (newHistory as Record<string, ConversationGroup[]>)[key];
        }
      });
      // Prepend to pinned (most recently pinned first)
      newHistory.pinned = [{ ...group, is_pinned: true }, ...(newHistory.pinned ?? [])];
    } else {
      // Remove from pinned
      newHistory.pinned = (newHistory.pinned ?? []).filter((item) => item._id !== group._id);
      if (!newHistory.pinned.length) delete (newHistory as Record<string, ConversationGroup[]>)["pinned"];
      // Restore to its time bucket
      const bucket = getTimeBucketKey(group);
      newHistory[bucket] = [{ ...group, is_pinned: false }, ...(newHistory[bucket] ?? [])];
    }

    setChatHistory(newHistory);

    try {
      await pinChatApi(brand, group._id, isPinned);
    } catch {
      setChatHistory(previousHistory);
    }
  };

  // Call the chat history API to fetch the chat history
  const callChatHistoryAPI = async () => {
    if (!user) {
      setChatHistory(null);
      return;
    }

    setIsLoadingMore(true);

    loadingRef.current = true;

    try {
      const history = await getChatHistory(
        brand,
        user.user_id,
        pageHistory + 1,
      );

      // Count the number of conversations in the history
      const conversationCount = Object.values(history).reduce(
        (total, group) => total + group.length,
        0,
      );

      // Count the number of conversations in the context
      const contextCount =
        chatHistory !== null
          ? Object.values(chatHistory).reduce(
              (total, group) => total + group.length,
              0,
            )
          : 0;

      if (typeof history === "object" && conversationCount === contextCount) {
        disableScrollRef.current = true;
        loadingRef.current = false;
        setDisableLoadMore(true);
        setIsLoadingMore(false);

        return;
      }

      setChatHistory(history as ChatHistory);
      loadingRef.current = false;
      setIsLoadingMore(false);

      setPageHistory((currentPage) => currentPage + 1);
    } catch (error) {
      console.log(error);
      setChatHistory(null);
      setIsLoadingMore(false);
    }
  };

  const sortPriority = [
    "today",
    "yesterday",
    "last_7_days",
    "last_30_days",
    "December",
    "November",
    "October",
    "September",
    "August",
    "July",
    "June",
    "May",
    "April",
    "March",
    "February",
    "January",
  ];

  // Sort keys: known priorities first, unknown keys numerically — exclude "pinned" (rendered separately)
  const sortedKeys =
    chatHistory !== null
      ? Object.keys(chatHistory).filter((k) => k !== "pinned").sort((a, b) => {
          const indexA = sortPriority.indexOf(a);
          const indexB = sortPriority.indexOf(b);

          const inA = indexA !== -1;
          const inB = indexB !== -1;

          // 1. Known keys first
          if (inA && inB) return indexA - indexB;
          if (inA) return -1;
          if (inB) return 1;

          // 2. Unknown string keys next (alphabetically)
          const isNumA = !isNaN(parseFloat(a)) && isFinite(parseFloat(a));
          const isNumB = !isNaN(parseFloat(b)) && isFinite(parseFloat(b));

          if (!isNumA && !isNumB) return a.localeCompare(b);
          if (!isNumA) return -1;
          if (!isNumB) return 1;

          // 3. Numeric keys last (numerical sort)
          return parseFloat(a) - parseFloat(b);
        })
      : [];

  // Close menu on click outside
  useEffect(() => {
    const closeMenu = (e: MouseEvent) => {
      if (
        menuRef.current instanceof HTMLUListElement &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setShowMenu(false);
      }
    };
    document.body.addEventListener("click", closeMenu);
    return () => document.body.removeEventListener("click", closeMenu);
  }, []);

  useEffect(() => {
    if (answers.length > 0) {
      setClickedIndex(answers[answers.length - 1][1].conversation_id);
    } else {
      setClickedIndex("");
    }
  }, [answers]);

  useEffect(() => {
    setDisableLoadMore(false);
  }, [brand]);

  return (
    <>
      <nav
        className={`${width <= 1280 && "absolute"} flex h-screen flex-col items-center gap-3.5 py-3.5 ${chatHistoryPanel ? "w-full border-r border-grayscale-opacity-blevel1 bg-grayscale-solid-g0 shadow-xl md:w-[275px]" : "w-[60px] sm:border-r sm:border-grayscale-opacity-blevel1 sm:bg-grayscale-solid-g0 sm:shadow-xl"} z-30 transition-width duration-[450ms] ease-in-out`}
        ref={sidebarRef}
      >
        <div
          className={`absolute left-3.5 z-[99] flex flex-col ${chatHistoryPanel ? "" : "gap-5"}`}
        >
          <HamburgerIcon
            className="mt-[3px] cursor-pointer select-none transition-all duration-[250ms] hover:scale-125"
            size="s2"
            color={`${chatHistoryPanel ? "Solid/Level7" : "Grayscale/Solid/G10"}`}
            onClick={() => {
              setChatHistoryPanel((prev) => !prev);
              setShowModalBrand(false);
            }}
          />
          {/* Hidden when sidebar is expanded */}
          <div
            className={`${chatHistoryPanel ? "hidden" : "hidden sm:block"} relative`}
          >
            <Tooltip text="Select Brand" position="right">
              <BrandIcon
                ref={brandIconRef}
                brand={brand}
                className={`${chatHistoryPanel ? "pointer-events-none" : "cursor-pointer"} transition-all duration-[250ms] hover:scale-125`}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowModalBrand((prev) => !prev);
                }}
              />
            </Tooltip>
            <Transition
              className={`${chatHistoryPanel && "pointer-events-none"} absolute -top-[5px] left-[50px]`}
              show={!chatHistoryPanel && showModalBrand}
              as="div"
              enter="transition ease-out duration-300"
              enterFrom="transform opacity-0"
              enterTo="transform opacity-100"
              leave="transition ease-in duration-300"
              leaveFrom="transform opacity-100"
              leaveTo="transform opacity-0"
            >
              <ModalBrand
                showModal={showModalBrand}
                setShowModal={setShowModalBrand}
                triggerRef={brandIconRef}
              />
            </Transition>
          </div>
          {/* Hidden when sidebar is expanded */}
          <Tooltip text="New Chat" position="right">
            <Ani
              className={`${
                chatHistoryPanel
                  ? "pointer-events-none hidden"
                  : "hidden cursor-pointer sm:block"
              } h-7 w-7 fill-grayscale-solid-g10 transition-all duration-[250ms] hover:scale-125 hover:fill-blue-solid-blue6`}
              onClick={() => clearChat()}
            />
          </Tooltip>
        </div>

        <Transition
          as="div"
          className="z-[98] w-full px-3.5"
          show={chatHistoryPanel}
          enter="transition ease-out-in delay-[200ms] duration-[250ms]"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition ease-out-in duration-100"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
          unmount={false}
        >
          <div className="flex w-full flex-col gap-3.5">
            <div className={`flex w-full justify-end`}>
              <BrandButton
                ref={brandButtonRef}
                showModal={showModalBrand}
                setShowModal={setShowModalBrand}
                rotation={width <= 700 ? "rotate-0" : "-rotate-90"}
              />
            </div>
            <ClearChatButton
              onClick={() => clearChat()}
              disabled={answers.length === 0}
            />
          </div>
        </Transition>

        <Transition
          className={`absolute left-3 top-12 z-[99] pr-3 md:left-[280px] md:top-3.5 md:pr-0`}
          show={chatHistoryPanel && showModalBrand}
          as="div"
          enter="transition ease-out duration-250"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition ease-in duration-250"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
          unmount={false}
        >
          <ModalBrand
            triggerRef={brandButtonRef}
            showModal={showModalBrand}
            setShowModal={setShowModalBrand}
          />
        </Transition>

        {isChatHistoryLoading && chatHistoryPanel && (
          <div className="flex items-center justify-center w-full h-full">
            <Spinner className="size-7 text-blue-solid-blue6" />
          </div>
        )}

        <Transition
          as={"div"}
          className="z-0 flex h-full flex-col items-center justify-center gap-2 px-3.5"
          show={
            chatHistoryPanel && chatHistory === null && !isChatHistoryLoading
          }
          enter="transition ease-out-in delay-[200ms] duration-[250ms]"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
        >
          <h6 className="whitespace-pre-wrap text-center font-medium leading-[150%] text-grayscale-solid-g10">
            {
              translation["Unlock Chat History" as keyof typeof translation][
                locale
              ]
            }
          </h6>
          <button
            className="w-full p-2 text-sm text-white rounded-md bg-blue-solid-blue6 hover:bg-blue-solid-blue5"
            onClick={() =>
              window.open(
                `${brand === "CLO3D" ? "https://clo3d.com/en/plans" : "https://style.clo-set.com/service/pricing"}`,
                "_blank",
              )
            }
          >
            {translation["Subscribe" as keyof typeof translation][locale]}
          </button>
        </Transition>

        <Transition
          show={
            chatHistoryPanel && !isChatHistoryLoading && chatHistory !== null
          }
          enter="transition ease-out-in delay-[200ms] duration-[250ms]"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition ease-out-in duration-100"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
        >
          <div className="scrollbar flex h-[calc(100vh-105px)] w-full flex-col gap-3.5 overflow-y-auto pl-3.5">
            <div className="flex w-full flex-col gap-5 p-2.5">
              {/* Pinned section — always rendered first when there are pinned conversations */}
              {(chatHistory?.pinned?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-sm font-semibold leading-[10px] text-grayscale-solid-g10">
                    {translation["Pinned" as keyof typeof translation]?.[locale] ?? "Pinned"}
                  </h6>
                  <ul className="flex flex-col gap-1">
                    {chatHistory?.pinned?.map((item) => {
                      const conversation = item.conversations[item.conversations.length - 1];
                      return (
                        <li key={item._id}>
                          {menuId === item._id && renameChat ? (
                            <input
                              type="text"
                              className="w-full rounded-md border border-[#333335] bg-grayscale-solid-g0 p-2 text-xs font-medium leading-[18px] text-grayscale-solid-g10"
                              autoFocus
                              defaultValue={conversation.question_summarization || conversation.question}
                              onBlur={(e) => {
                                setRenameChat(false);
                                setMenuId("");
                                handleRename(conversation._id, e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  setRenameChat(false);
                                  setMenuId("");
                                  handleRename(conversation._id, e.currentTarget.value);
                                }
                              }}
                            />
                          ) : (
                            <div
                              className={`group flex cursor-pointer items-center justify-center gap-2 rounded-xl p-2 ${
                                clickedIndex === item._id ? "bg-grayscale-solid-g2" : "hover:bg-grayscale-solid-g2"
                              }`}
                              onClick={() => {
                                if (clickedIndex !== item._id) {
                                  handleChatConversation(item);
                                  setClickedIndex(item._id);
                                }
                              }}
                            >
                              <Pin className="size-3 flex-shrink-0 fill-grayscale-solid-g7" />
                              <div className="color-[#333335] line-clamp-1 flex-1 text-[12px] font-medium leading-[18px] text-grayscale-solid-g10">
                                {conversation.question_summarization || conversation.question}
                              </div>
                              <div
                                className="relative group"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (menuId === item._id) {
                                    setShowMenu(!showMenu);
                                  } else {
                                    setShowMenu(true);
                                  }
                                  setMenuId(item._id);
                                }}
                              >
                                <Ellipsis
                                  className={`size-5 ${
                                    clickedIndex === item._id ? "fill-grayscale-solid-g10" : "fill-grayscale-solid-g0"
                                  } group-hover:fill-grayscale-solid-g10`}
                                />
                                {menuId === item._id && showMenu && (
                                  <SideBarHistoryMenu
                                    ref={menuRef}
                                    isPinned={true}
                                    setRenameChat={setRenameChat}
                                    handlePinChat={() => {
                                      setShowMenu(false);
                                      handlePinChat(item, false);
                                    }}
                                    handleConversationDelete={() => {
                                      setOpenDeleteModal(true);
                                      setDeleteChatId(item._id);
                                    }}
                                    onMouseLeave={() => setShowMenu(false)}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {sortedKeys.map((key) => {
                return (
                  sortedKeys.length > 0 && (
                    <div
                      key={key}
                      className={`flex flex-col gap-2.5 ${key === sortedKeys[sortedKeys.length - 1] && ""}`}
                    >
                      <h6 className="text-sm font-semibold leading-[10px] text-grayscale-solid-g10">
                        {key.replaceAll("_", " ") in translation
                          ? translation[
                              key.replaceAll(
                                "_",
                                " ",
                              ) as keyof typeof translation
                            ][locale]
                          : key.charAt(0).toUpperCase() + key.slice(1)}
                      </h6>
                      <ul className="flex flex-col gap-1">
                        {chatHistory &&
                          chatHistory[key as keyof ChatHistory]?.map((item) => {
                            const conversation =
                              item["conversations"][
                                item.conversations.length - 1
                              ];
                            return (
                              <li key={item._id}>
                                {menuId === item._id && renameChat ? (
                                  <input
                                    type="text"
                                    className={`w-full rounded-md border border-[#333335] bg-grayscale-solid-g0 p-2 text-xs font-medium leading-[18px] text-grayscale-solid-g10`}
                                    autoFocus
                                    defaultValue={
                                      conversation.question_summarization ||
                                      conversation.question
                                    }
                                    onBlur={(e) => {
                                      setRenameChat(false);
                                      setMenuId("");
                                      handleRename(
                                        conversation._id,
                                        e.target.value,
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        setRenameChat(false);
                                        setMenuId("");
                                        handleRename(
                                          conversation._id,
                                          e.currentTarget.value,
                                        );
                                      }
                                    }}
                                  />
                                ) : (
                                  <div
                                    className={`group flex cursor-pointer items-center justify-center gap-2 rounded-xl p-2 ${
                                      clickedIndex === item._id
                                        ? "bg-grayscale-solid-g2"
                                        : "hover:bg-grayscale-solid-g2"
                                    } `}
                                    onClick={(e) => {
                                      // Prevent the user from clicking on the current conversation
                                      if (clickedIndex !== item._id) {
                                        handleChatConversation(item);
                                        setClickedIndex(item._id);
                                      }
                                    }}
                                  >
                                    <div
                                      className={`color-[#333335] line-clamp-1 flex-1 text-[12px] font-medium leading-[18px] text-grayscale-solid-g10`}
                                    >
                                      {conversation.question_summarization ||
                                        conversation.question}
                                    </div>
                                    <div
                                      className="relative group"
                                      onClick={(e) => {
                                        // Prevent the user from clicking on the current conversation
                                        e.stopPropagation();

                                        if (menuId === item._id) {
                                          setShowMenu(!showMenu);
                                        } else {
                                          setShowMenu(true);
                                        }

                                        setMenuId(item._id);
                                      }}
                                    >
                                      <Ellipsis
                                        className={`size-5 ${
                                          clickedIndex === item._id
                                            ? "fill-grayscale-solid-g10"
                                            : "fill-grayscale-solid-g0"
                                        } group-hover:fill-grayscale-solid-g10`}
                                      />
                                      {menuId === item._id && showMenu && (
                                        <SideBarHistoryMenu
                                          ref={menuRef}
                                          isPinned={false}
                                          setRenameChat={setRenameChat}
                                          handlePinChat={() => {
                                            setShowMenu(false);
                                            handlePinChat(item, true);
                                          }}
                                          handleConversationDelete={() => {
                                            setOpenDeleteModal(true);
                                            setDeleteChatId(item._id);
                                          }}
                                          onMouseLeave={() => {
                                            setShowMenu(false);
                                          }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  )
                );
              })}
              {chatHistory !== null && Object.keys(chatHistory).length > 0 && (
                <div className="flex justify-center pb-4">
                  {!isLoadingMore ? (
                    <button
                      className={`w-full rounded-[300px] bg-grayscale-solid-g1 p-2 text-sm font-semibold text-grayscale-solid-g10 hover:bg-blue-opacity-level2 ${disableLoadMore && "cursor-not-allowed"}`}
                      onClick={async () => {
                        await callChatHistoryAPI();
                      }}
                      disabled={disableLoadMore}
                    >
                      {!disableLoadMore
                        ? translation["Load More" as keyof typeof translation][
                            locale
                          ]
                        : translation[
                            "No More History" as keyof typeof translation
                          ][locale]}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="flex items-center justify-center w-full p-2 text-sm font-semibold transition duration-150 ease-in-out rounded-md cursor-not-allowed bg-grayscale-solid-g1 text-grayscale-solid-g10 hover:bg-blue-opacity-level2"
                      disabled
                    >
                      <Spinner className="mr-3 -ml-1 size-5 text-blue-solid-blue6" />
                      {
                        translation[
                          "Processing..." as keyof typeof translation
                        ][locale]
                      }
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </Transition>
      </nav>

      <Modal
        className="w-[250px] md:w-[400px]"
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
      >
        <ModalDelete
          deleteChat={async () => {
            await handleConversationDelete(deleteChatId);

            error && setError({} as AxiosError);
            setCitation(undefined);
            setClickedIndex("");

            // Clear transcript only when the deleted thread is the one currently loaded in answers
            if (answers.some((a) => a[1].conversation_id === deleteChatId)) {
              setLastQuestion(undefined);
              setAnswers([]);
              setPage(Page.HOME);
            }

            setConversationID("");
          }}
          setOpenDeleteModal={setOpenDeleteModal}
        />
      </Modal>
    </>
  );
};

export default SideBarHistory;
